import { type MouseEvent, useMemo, type VoidFunctionComponent } from 'react';
import type { NodeRendererProps } from 'react-arborist';

import { FileTreeNode } from '../../../generic/FileTree';
import { useNestingLines } from '../../../generic/FileTree/hooks';
import type { BinaryComponentPathNode } from '../../../../../shared/types/routes/binary';

export type BinaryTreeNodeData = BinaryComponentPathNode & {
  children?: BinaryTreeNodeData[];
  hasLoaded?: boolean;
};

const BinaryTreeNode: VoidFunctionComponent<NodeRendererProps<BinaryTreeNodeData>> = ({
  node,
  style,
}) => {
  const isDirectory = node.data.type === 'directory';

  //  Handle clicking on the entire row.
  const handleSelectRow = (event: MouseEvent) => {
    //  Handle selecting a node.
    node.handleClick(event);
  };

  const handleToggleDirectory = () => {
    //  "isInternal" is a react-arborist-ism for "this is a directory".
    //  Calling `node.toggle` will toggle the open/closed state of these nodes.
    if (node.isInternal) {
      node.toggle();
    }
  };

  let nodeText = node.data.name;
  if (node.data.type === 'file' && node.data.packageName && node.data.version) {
    nodeText = `${node.data.name} (${node.data.packageName}@${node.data.version})`;
  } else if (node.data.type === 'directory' && node.data.count !== undefined) {
    nodeText = `${node.data.name} (${node.data.count})`;
  }

  // Generate nesting lines background for the current node level
  const nestingLinesBackgroundStyles = useNestingLines(node.level);
  const enhancedStyle = useMemo(() => ({
    ...style,
    ...nestingLinesBackgroundStyles,
  }), [nestingLinesBackgroundStyles, style]);

  return (
    <FileTreeNode
      style={enhancedStyle}
      onClick={handleSelectRow}
      isSelected={node.isSelected}
      isDirectory={isDirectory}
    >
      <FileTreeNode.Arrow
        isOpen={node.isOpen}
        isDirectory={isDirectory}
        onToggleDirectory={handleToggleDirectory}
      />
      {!isDirectory && (<FileTreeNode.Icon />)}
      <FileTreeNode.Text text={nodeText} />
    </FileTreeNode>
  );
};

export default BinaryTreeNode;
